<template>
  <div class="edit-selected-container" data-app>
    <div class="edit-selected-form scroll-fire-fox">
      <div
        v-for="field in fields"
        :key="field.id"
        class="edit-selected-field-container"
      >
        <label :for="field.label" :class="{ required: field.required }">
          {{ field.label }}
        </label>
        <div v-if="field.disable" class="edit-selected-field">
          {{ currentValues[field.var_name] | fillField }}
        </div>

        <div
          v-else-if="
            hasMultipleValues(field) && field.input_type !== 'checkbox'
          "
          class="edit-selected-field editable"
          @click="makeMultipleValuesFieldEditable(field)"
        >
          {{ $t("MultipleValuesFound") }}<br />
          <b>{{ $t("ClickToEdit") }}</b>
        </div>

        <input
          v-else-if="isStandardInput(field.input_type)"
          :type="getSpecificStandardInputType(field.input_type)"
          :id="field.label"
          :value="getValue(currentValues, field)"
          @change="updateValue(field, $event.target.value)"
          :class="{
            'required-not-filled': requiredWasMadeEmpty(field)
          }"
        />

        <treeselect
          v-else-if="isSelectInput(field.input_type)"
          :value="getValue(currentValues, field)"
          @input="updateValue(field, $event)"
          :options="getSpecificListOfOptions(field)"
          :clearable="selectIsClearable(field)"
          :disabled="field.multi_select_disable"
          class="treeselect"
          :class="{
            'required-not-filled': requiredWasMadeEmpty(field)
          }"
          disableFuzzyMatching
        />
        <treeselect
          v-else-if="field.input_type === 'user'"
          :value="getValue(currentValues, field)"
          @input="updateValue(field, $event)"
          :options="optionsUser"
          :normalizer="userNormalizer"
          :clearable="selectIsClearable(field)"
          :disabled="field.multi_select_disable"
          class="treeselect"
          :class="{
            'required-not-filled': requiredWasMadeEmpty(field)
          }"
          disableFuzzyMatching
        />
        <div v-if="field.input_type === 'checkbox'">
          <v-checkbox
            :input-value="getValue(currentValues, field)"
            @change="updateValue(field, $event)"
            :ripple="false"
            :disabled="field.disable"
            :indeterminate="hasMultipleValues(field)"
            :false-value="0"
            :true-value="1"
            :hide-details="true"
            disableFuzzyMatching
          />
        </div>

        <div @click="resetFieldValue(field)" class="revert-button">
          <RevertIcon v-show="showCancelEdit(field)" class="revert-icon" />
        </div>
      </div>
    </div>
    <div class="saveDiv-buttons-container">
      <input
        @click="closeEditSelected('local')"
        type="submit"
        :value="$t('Cancel')"
        class="form-input form-next form-previous"
      />
      <input
        @click="leaveEditSelect('apply')"
        type="submit"
        :value="$t('ApplyChanges')"
        class="form-input form-next"
      />
    </div>
    <app-dialog-box
      :open="leaveEditSelectedDialog"
      :title="$t('LeaveEditSelected')"
      :content="
        $t('UnsavedChanges') +
          '\n' +
          $t('CheckLeaveEditSelected') +
          '\n' +
          $t('ChangesWillBeLost')
      "
      :main-action-label="$t('No')"
      @mainAction="leaveEditSelectedDialog = false"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="$emit('close')"
    />
  </div>
</template>

<script>
import { bus } from "@/main.js";
import { multiSelectionMixin } from "@/mixins/multiSelectionMixin";
import RevertIcon from "@/assets/revert.svg";
import AppDialogBox from "@/components/_Molecules/AppDialogBox";
import _ from "lodash";

export default {
  name: "MultiSelectionEditSelected",

  components: { AppDialogBox, RevertIcon },

  filters: {
    fillField(value) {
      if (value === null || value === undefined) {
        return "No value found";
      } else if (value === "multipleValues") {
        return "Multiple Values Found";
      } else return value;
    }
  },
  mixins: [multiSelectionMixin],

  props: {
    fields: {
      type: Array,
      required: true
    },
    objects: {
      type: Array,
      required: true
    },
    selected: {
      type: Array,
      required: true
    },
    options: {
      type: Object,
      required: true
    },
    optionsUser: {
      type: Array
    }
  },

  data() {
    return {
      originalValues: {},
      currentValues: {},
      editedValues: {},
      leaveEditSelectedDialog: false
    };
  },

  mounted() {
    bus.$on("startEditSelectedDeactivation", origin =>
      this.closeEditSelected(origin)
    );

    this.$nextTick(() => {
      this.originalValues = this.getOriginalValues();
      this.currentValues = this.getOriginalValues();
    });
  },

  methods: {
    /**
     * Gets the values for each field of the collection of selected objects.
     * @returns {Object}
     */
    getOriginalValues() {
      let originalValues = {};
      let nrOfSelectedObjects = this.selected.length;
      this.fields.forEach(field => {
        let key = field.var_name;
        originalValues[key] = this.getFieldOriginalValue(
          key,
          nrOfSelectedObjects
        );
      });
      return originalValues;
    },

    /**
     * Gets the value of a given field based on the values for that field of the selected objects.
     * @param {String} key - name of the variable associated to the field.
     * @param {Number} nrOfSelectedObjects - number of objects previously selected.
     * @returns {*} the value of the field from the first object if all objects have the same value, or
     * 'multipleValues' if there are objects with different values.
     */
    getFieldOriginalValue(key, nrOfSelectedObjects) {
      let fieldValue = this.objects[this.selected[0]][key];
      for (let i = 0; i < nrOfSelectedObjects; i++) {
        let objectIndex = this.selected[i];
        let objectValue = this.objects[objectIndex][key];
        if (objectValue !== fieldValue) {
          return "multipleValues";
        }
      }
      return fieldValue;
    },

    /**
     * Checks if the field has more than one value.
     * @param {Object} field
     * @returns {boolean}
     */
    hasMultipleValues(field) {
      let value = this.getValue(this.currentValues, field);
      return value === "multipleValues";
    },

    /**
     *Makes a field with multiple values editable and sets it's value to null.
     * @param {Object} field - field to make editable
     */
    makeMultipleValuesFieldEditable(field) {
      let key = field.var_name;
      let value = null;
      if (field.hasOwnProperty("default_value")) {
        value = field.default_value;
      }
      this.currentValues[key] = value;
    },

    /**
     * Determines, for each field, if the 'Cancel Edit' button should be shown
     * @param field
     * @returns {boolean}
     */
    showCancelEdit(field) {
      return this.editedValues.hasOwnProperty(field.var_name);
    },

    /**
     * Resets a given field to it's original value
     * @param field
     */
    resetFieldValue(field) {
      this.currentValues[field.var_name] = this.originalValues[field.var_name];
      delete this.editedValues[field.var_name];
    },

    /**
     * Updates the value of a specific field.
     * @param field
     * @param value
     */
    updateValue(field, value) {
      _.set(this.currentValues, field.var_name, value);
      let originalValue = this.getValue(this.originalValues, field);
      if (value !== originalValue) {
        _.set(this.editedValues, field.var_name, value);
      }
    },

    /**
     * Checks if the value of a required field was removed.
     * @param {Object} field - field being edited
     * @returns {boolean}
     */
    requiredWasMadeEmpty(field) {
      return (
        field.required &&
        (this.currentValues[field.var_name] === "" ||
          this.currentValues[field.var_name] === null)
      );
    },

    closeEditSelected(origin) {
      if (origin === "local" || origin === "fromToolbar") {
        if (Object.keys(this.editedValues).length === 0) {
          this.leaveEditSelect("close");
        } else {
          this.leaveEditSelectedDialog = true;
        }
      } else {
        this.leaveEditSelect("close");
      }
    },

    leaveEditSelect(action) {
      if (action === "close") {
        this.$emit(action);
      } else if (action === "apply") {
        this.$emit(action, this.editedValues);
      }
    }
  },

  destroyed() {
    this.originalValues = this.currentValues = this.editedValues = {};
    bus.$off("startEditSelectedDeactivation");
  }
};
</script>

<style scoped>
.saveDiv-buttons-container {
  display: flex;
  justify-content: flex-end;
  height: 60px;
  margin-top: 10px;
}

.edit-selected-container {
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: #f0f0ef;
  border: 0.5px solid rgba(0, 0, 0, 0.15);
  border-radius: 0 0 5px 5px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.edit-selected-form {
  width: 100%;
  height: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-wrap: wrap;
}
.edit-selected-field-container {
  width: 50%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
}
.edit-selected-field-container label {
  width: 45%;
  font-weight: bold;
  text-align: left;
}

.edit-selected-field,
.edit-selected-field-container input {
  width: 50%;
  height: 32px;
  border-radius: 2px;
  padding: 5px 10px;
  text-align: left;
  background: white;
  border: 1px solid transparent;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  line-height: 12px;
}
.edit-selected-field-container input {
  border: 1px solid grey;
}

.edit-selected-form .treeselect {
  width: 50%;
  background-color: white;
}

.edit-selected-form .treeselect input {
  background-color: transparent;
}

.edit-selected-form .required-not-filled,
.edit-selected-form .treeselect.required-not-filled {
  background-color: #cc654b;
}

.edit-selected-field.editable {
  cursor: pointer;
}

.revert-button {
  width: 20px;
  cursor: pointer;
}
.revert-icon {
  width: 12px;
}
.revert-icon:hover path {
  fill: var(--primary-highlight);
}
</style>

<style>
.edit-selected-form .vue-treeselect__control {
  border-radius: 2px;
  border: 1px solid grey;
  background-color: transparent;
}
input:invalid,
textarea:invalid {
  border-bottom: 1px solid var(--red);
}
</style>



// WEBPACK FOOTER //
// src/components/Home/Forms/MultiSelectionForm/MultiSelectionEditSelected.vue